<template>
  <section
    id="section--search"
    class="section section--search"
    :class="[`is-${urlParams.type}`]"
  >
    <div id="container-section-search-navbar">
      <LadBreadcrumb />

      <LadAffix
        v-if="!isOutlet && !isBuenFin"
        relative-element-selector="#container-section-search-navbar"
        :offset="{ top: -45, bottom: 60 }"
      >
        <LadSearchNavbar
          :is-country="isCountry"
          :is-continent="isContinent"
          :is-city="isCity"
          class="vue-affix"
        />
      </LadAffix>

      <el-container class="country-header">
        <el-main>
          <template v-if="isContinent || isCountry">
            <h1 v-if="urlParams.tab === 'trips'">{{
              $t(`${base}.cover.information_of`, { name: header.name })
            }}</h1>
            <div v-else-if="urlParams.tab === 'flights'">
              <h1>{{
                $t(`${base}.title.flights_to`, { name: header.name })
              }}</h1>
              <h2>{{
                $t(`${base}.title.flights_discover`, { name: header.name })
              }}</h2>
            </div>
            <div v-else>
              <h1>{{ isContinent ? getContinentTitle : getCountryTitle }}</h1>
              <h2>{{ $t(`${base}.title.h2`, { name: header.name }) }}</h2>
            </div>
          </template>

          <template v-else>
            <h1 v-if="isOutlet">{{ $t(`${base}.meta.title`) }}</h1>
            <h1 v-else-if="isBuenFin">{{ $t('buen_fin.meta.title') }}</h1>
            <h1 v-else-if="isMountainsInMexico">{{
              $t(`${base}.meta.title`)
            }}</h1>
            <h1 v-else-if="isCategory">{{ header.name }}</h1>
            <div v-else-if="isCity">
              <template v-if="urlParams.tab === 'flights'">
                <h1>{{
                  $t(`${base}.title.flights_to`, { name: header.name })
                }}</h1>
                <h2>{{
                  $t(`${base}.title.flights_discover`, { name: header.name })
                }}</h2>
              </template>
              <template v-else>
                <h1>{{
                  $t(`${base}.title.continent`, { name: header.name })
                }}</h1>
                <h2>{{ $t(`${base}.title.h2`, { name: header.name }) }}</h2>
              </template>
            </div>
            <h1 v-else>{{
              $t(`${base}.cover.results_on`, { name: searchForm.search })
            }}</h1>

            <h2 v-if="isOutlet" class="hero-description-outlet">
              {{ getDescriptionOutlet }}</h2
            >
            <h2 v-else-if="isBuenFin" class="hero-description-outlet">
              {{ $t('buen_fin.meta.description') }}</h2
            >
            <p
              v-else-if="
                isMountainsInMexico || isCategory || isContinent || isCity
              "
              class="hero-description"
            >
              {{ header.description }}
            </p>
          </template>
        </el-main>
      </el-container>

      <LadHomeDestinations
        v-if="isBuenFin"
        cache-prefix="home-destinations"
        :cache-key="cacheKey"
        :base="`home.destinations_buen_fin`"
      />

      <div
        v-else
        class="experiences-section"
        :class="{
          'container-section-search-content': !isCountry,
        }"
      >
        <LadSearchFlightsTab
          v-if="
            (isCountry || isContinent || isCity) && urlParams.tab === 'flights'
          "
        />

        <el-container v-else>
          <section v-if="isMobile" class="wrapper-open-filter">
            <div class="open-filter">
              <el-button
                plain
                type="primary"
                icon="fas fa-filter"
                @click="openFilter"
                >{{ `Filtrar por` }}</el-button
              >
            </div>
          </section>
          <el-aside v-else width="320px" class="lad--custom__filters">
            <LadSearchFilters />
          </el-aside>

          <el-main>
            <LadContainerMediaText
              v-if="!items.length"
              :title="$t(`${base}.without_results.title`)"
              :with-container="false"
              svg="local-adventures-without-results"
            >
              <div slot="description" class="description">
                <p>{{ $t(`${base}.without_results.description`) }}</p>
              </div>
            </LadContainerMediaText>

            <template v-if="withoutItems">
              <LadExperiencesTop
                :with-container="false"
                cache-prefix="search-ex-top"
                :cache-key="cacheKey"
              />

              <LadExperiencesRandom :with-container="false" />
            </template>

            <template v-else>
              <LadSearchExperiencesOutlet
                :items="items"
                :route-name="routeName"
                :is-custom="true"
              />

              <div class="wrapper-paginate">
                <el-pagination
                  :current-page="paginator.page"
                  :page-sizes="paginator.sizes"
                  :page-size="paginator.limit"
                  :layout="paginator.layout"
                  :total="paginator.total"
                  :pager-count="5"
                  background
                  :hide-on-single-page="true"
                  @current-change="handleCurrentChange"
                  @size-change="handleSizeChange"
                >
                  <template slot="default">
                    <LadPeger />
                  </template>
                </el-pagination>
              </div>
            </template>

            <LadSearchFastLinks
              v-if="isContinent || (isCountry && getCitiesInCountry.length)"
              :location-name="header.name"
              :items="getCitiesInCountry"
              :type="isContinent ? 'continent' : 'country'"
              :durations="header.durations"
            />

            <LadSearchReviewsLegacy
              v-if="(isCountry || isCity) && header.reviews.length"
              :name="header.name"
              :reviews="header.reviews"
            />
          </el-main>
        </el-container>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { objet2qs } from '@/utils'
import StaticMetaMixin from '@/utils/mixin/static-meta'
import {
  SEARCH_TYPE_ROUTER,
  SEARCH_COUNTRY_TYPE_ROUTER,
} from '@/utils/static-router-alias'
import LadPeger from './partials/pager'
import LadSearchFlightsTab from './partials/flights'
import LadSearchFastLinks from './partials/fast-links'
import LadSearchReviewsLegacy from './partials/reviews-legacy'

export default {
  name: 'LadSearchCommon',
  components: {
    LadPeger,
    LadSearchFlightsTab,
    LadSearchFastLinks,
    LadSearchReviewsLegacy,
    LadBreadcrumb: () =>
      import(
        /* webpackChunkName: "lad-breadcrumb" */ '@/components/breadcrumb/index'
      ),
    LadContainerMediaText: () =>
      import(
        /* webpackChunkName: "lad-container-media-text" */ '@/components/container-media-text/index'
      ),
    LadSearchNavbar: () =>
      import(
        /* webpackChunkName: "lad-search-navbar" */ '@/components/navbars/search'
      ),
    LadSearchFilters: () =>
      import(
        /* webpackChunkName: "lad-search-filters" */ '@/components/pages/search/partials/filters'
      ),
    // LadSearchExperiences: () =>
    //   import(
    //     /* webpackChunkName: "lad-search-experiences" */ '@/components/pages/search/partials/experiences'
    //   ),
    LadSearchExperiencesOutlet: () =>
      import(
        /* webpackChunkName: "lad-search-experiences-outlet" */ '@/components/pages/search/partials/experiences-outlet'
      ),
    LadExperiencesTop: () =>
      import(
        /* webpackChunkName: "lad-home-top-experiences" */ '@/components/experiences/top'
      ),
    LadExperiencesRandom: () =>
      import(
        /* webpackChunkName: "lad-home-top-random" */ '@/components/experiences/random'
      ),
    LadHomeDestinations: () =>
      import(
        /* webpackChunkName: "lad-home-destinations" */ '@/components/pages/home/partials/destinations/index'
      ),
  },
  async asyncData({ app, store, route }) {
    store.dispatch('search/setCover', {
      media: {
        src: `${process.env.DO_SPACES_CDN_ENDPOINT}/web-client/covers/expedicion-volcanes.jpg`,
      },
    })

    /**
     * el metodo `getData` y `asyncData` tienen exactamente la misma logica
     * lo que uno es SSR y otro es para interactuar con los filtros
     */

    const hasQuery = !!route.query
    const qParams = route.params || {}
    const hasParams = !!qParams.type && !!qParams.value

    if (Object.entries(route.query).length === 0 || route.name === 'search') {
      await store.dispatch('search/updatePaginate', {
        page: 1,
        limit: 30,
        total: 0,
      })
      await store.dispatch('search/setForm', {
        filters: {
          age: [],
          category: [],
          date: [],
          duration: [],
          month: [],
          city: [],
          language: [],
          price: { min: null, max: null, type: 'price' },
        },
      })
    }

    let params = { tab: undefined, type: undefined, value: undefined }

    let loading = false
    let loaded = false
    let base = 'search'
    let days = null

    switch (route.name) {
      case 'outlet-test':
      case 'buen-fin':
      case 'outlet':
        params = { tab: 'experiences', type: 'outlet', value: 'experiences' }

        base = 'outlet'

        await store.dispatch('search/setForm', {
          sort: 'more_popular__desc',
        })
        break
      case 'c-mountains-in-mexico':
        params = {
          tab: 'experiences',
          type: 'mountains-in-mexico',
          value: 'experiences',
        }

        base = 'mountains_in_mexico'
        break
      case 'search':
        // eslint-disable-next-line no-case-declarations
        const tab =
          hasQuery && route.query.tab ? route.query.tab : 'experiences'
        // eslint-disable-next-line no-case-declarations
        const type =
          hasQuery && route.query.type ? route.query.type : 'experience'
        // eslint-disable-next-line no-case-declarations
        const value = hasQuery ? route.query.q || route.query.value : ''

        params = { tab, type, value }
        break
      case 'search-type':
      default:
        params = {
          tab: qParams.tab,
          type: qParams.type,
          value: qParams.value,
        }
        if (qParams.days !== undefined && typeof qParams.days === 'string') {
          const regex = new RegExp('^\\d?\\d')
          days = qParams.days.match(regex)
        }
        break
    }

    if (typeof SEARCH_TYPE_ROUTER.base[params.type] === 'string') {
      params.type = SEARCH_TYPE_ROUTER.base[params.type]
    }

    if (typeof SEARCH_TYPE_ROUTER.base[params.tab] === 'string') {
      params.tab = SEARCH_TYPE_ROUTER.base[params.tab]
    }

    /**
     * Transforms the given filters object into a new object with nested properties.
     *
     * @param {Object} filters - The filters object to be transformed.
     * @return {Object} The transformed filters object.
     */
    const transformFilters = (filters) => {
      const result = {}

      Object.keys(filters).forEach((key) => {
        if (key.includes('.')) {
          const [parent, child] = key.split('.')

          if (!result[parent]) {
            result[parent] = {}
          }
          result[parent][child] = filters[key]
        } else {
          result[key] = filters[key]
        }
      })

      return result
    }

    // eslint-disable-next-line no-case-declarations
    const filters = hasQuery
      ? transformFilters(Object.assign({}, route.query))
      : {}

    const sort = route.query.sort ? route.query.sort : null
    if (days !== null) {
      filters.duration = [days[0], days[0]]
    }

    delete filters.tab
    delete filters.q
    delete filters.type
    delete filters.page
    delete filters.limit
    delete filters.value

    if (hasQuery) {
      await store.dispatch('search/updatePaginate', {
        page: route.query.page,
        limit: route.query.limit,
      })

      Object.keys(filters).forEach((k) => {
        if (['price'].includes(k)) {
          Object.keys(filters[k]).forEach((key) => {
            if (['min', 'max'].includes(key)) {
              filters[k][key] = parseInt(filters[k][key])
            }
          })
        } else if (!['value', 'q', 'sort', 'page', 'limit'].includes(k)) {
          /** transformando siempre a array los paramentros get */
          if (!Array.isArray(filters[k])) {
            filters[k] = ['category'].includes(k)
              ? [filters[k]]
              : [filters[k], filters[k]]
          }

          /** tranformando string a int */
          if (!['date', 'category', 'city', 'language'].includes(k)) {
            filters[k] = filters[k].map((item) => parseInt(item))
          }
        }
      })

      await store.dispatch('search/setForm', {
        search: route.name === 'search' ? params.value : '',
        filters,
        sort,
      })
    }

    if (hasParams || hasQuery) {
      loading = true

      const data = {
        tab: params.tab,
        type: params.type,
        value: params.value,
        form: store.state.search.form,
      }

      if (store.state.search.header.permalink.default === params.value) {
        /** */
      } else if (
        ['search-type', 'search'].includes(route.name) &&
        ['city', 'country', 'continent'].includes(params.type)
      ) {
        await store.dispatch('search/getHeader', {
          type: params.type,
          value: params.value,
        })
      } else if (route.name !== 'search-type') {
        await store.dispatch('search/updateBreadcrumbs', [])
      }

      if (
        SEARCH_TYPE_ROUTER.base[params.tab] === 'tours' ||
        route.name !== 'search-type'
      ) {
        await store.dispatch('search/getExperiences', data)
      }

      loading = false
      loaded = true
    }

    const breadcrumb = []
    const items = store.state.search.breadcrumbs
    if (Object.keys(items).length > 0) {
      const keys = ['continent', 'country', 'city']

      keys.forEach((key) => {
        if (items[key]) {
          const tab = ['trips', 'tours', 'flights'].includes(params.tab)
            ? params.tab
            : 'tours'

          const to = {
            name: key === 'country' ? 'search-country' : 'search-type',
            params: {
              type: app.i18n.t(`router.search_type.type.${key}`),
              value: items[key].slug,
              tab:
                key === 'country'
                  ? null
                  : app.i18n.t(`router.search_type.tab.${tab}`),
              travelTo: app.i18n.t('router.search_type.travel_to_'),
            },
          }

          breadcrumb.push({ to, text: items[key].name })
        }
      })
    }

    if (route.name === 'search' && params.type === 'country') {
      const to = {
        name: 'search-country',
        params: {
          type: app.i18n.t('router.search_type.type.country'),
          value: params.value,
          travelTo: app.i18n.t('router.search_type.travel_to_'),
        },
      }

      breadcrumb.push({ to, text: store.state.search.header.name })

      breadcrumb.push({
        to: null,
        text: app.i18n.t(`router.search_type.tab.trips`),
      })
    } else if (route.name === 'search-type') {
      if (SEARCH_TYPE_ROUTER.groups.category.includes(params.type)) {
        const name = store.state.search.header.name.replace('-', '_')
        breadcrumb.push({
          to: null,
          text: app.i18n.t(`general.categories.${name}`),
        })
      } else {
        const to = {
          name: 'search-type',
          params: {
            type: app.i18n.t(`router.search_type.type.${params.type}`),
            value: params.value,
            tab: app.i18n.t(`router.search_type.tab.${params.tab}`),
          },
        }

        breadcrumb.push({ to, text: store.state.search.header.name })
      }

      if (params.tab) {
        const text = app.i18n.t(`router.search_type.tab.${params.tab}`)

        breadcrumb.push({
          to: null,
          text,
        })
      }
    } else if (route.name === 'outlet') {
      breadcrumb.push({
        to: null,
        text: app.i18n.t(`outlet.meta.title`),
      })
    } else if (route.name === 'buen_fin-viajes') {
      breadcrumb.push({
        to: null,
        text: app.i18n.t(`buen_fin.meta.title`),
      })
    } else {
      breadcrumb.push({
        to: null,
        text: app.i18n.t(`general.search`),
      })
    }

    await store.dispatch('app/setBreadcrumb', breadcrumb)

    return { urlParams: params, loading, loaded, base }
  },
  data() {
    return {
      base: 'search',
      loading: false,
      loaded: false,
      urlParams: {
        tab: undefined,
        type: undefined,
        value: undefined,
      },
      lazy: {
        navbar: false,
        list: false,
        countryInfo: false,
      },
      routeName: 'product-permalink',
      activeFaqs: [],
    }
  },
  computed: {
    ...mapState('app', ['breadcrumb']),
    ...mapState('search', {
      header: 'header',
      searchForm: 'form',
      continent: (state) =>
        Object.keys(state.breadcrumbs).length
          ? state.breadcrumbs.continent.name
          : null,
      hasDescription: (state) => state.header.description,
    }),
    ...mapState('settings', ['currency', 'countries']),
    ...mapGetters('settings', ['language']),
    ...mapGetters('device', ['isMobile']),
    items() {
      return this.$store.state.search.items || []
    },
    withoutItems() {
      return this.loaded && this.items.length === 0
    },
    isCountry() {
      return SEARCH_COUNTRY_TYPE_ROUTER.groups.country.includes(
        this.$route.params.type || this.$route.query.type
      )
    },
    isContinent() {
      return SEARCH_TYPE_ROUTER.groups.continent.includes(
        this.$route.params.type
      )
    },
    isOutlet() {
      return ['outlet', 'outlet-test'].includes(this.$route.name)
    },
    isBuenFin() {
      return ['buen-fin'].includes(this.$route.name)
    },
    isOutletTest() {
      return /exp1/.test(this.$route.params.test)
    },
    isMountainsInMexico() {
      return this.$route.name === 'c-mountains-in-mexico'
    },
    isSearch() {
      return this.$route.name === 'search'
    },
    isCategory() {
      return SEARCH_TYPE_ROUTER.groups.category.includes(
        this.$route.params.type
      )
    },
    isCity() {
      return SEARCH_TYPE_ROUTER.groups.city.includes(this.$route.params.type)
    },
    cacheKey() {
      const name = this.$route.path.replace(/\//g, '-').replace('-', '')

      return `${name}--${this.language}-${this.currency}--${
        this.isMobile ? 'm' : 'd'
      }`
    },
    paginator() {
      return {
        page: this.searchForm.page,
        limit: this.searchForm.limit,
        total: this.searchForm.total,
        sizes: [30, 50, 100, 150],
        layout: 'total, sizes, slot, pager, slot, jumper',
      }
    },
    value() {
      return this.$route.params.value
    },
    getDescriptionOutlet() {
      let description = this.$t(`${this.base}.meta.description`)

      if (this.paginator.page > 1) {
        description = `${this.$t(`${this.base}.page`)} ${
          this.paginator.page
        }: ${description}`
      }

      return description
    },
    getCitiesInCountry() {
      let cities = []

      if (this.isCountry) {
        const country = this.countries.find(
          ({ slug }) => this.header.permalink.default === slug
        )
        if (country) cities = country.cities || []
      }

      return cities
    },
    getStructuredDataBreadcrumb() {
      const itemListElement = this.breadcrumb
        .map((_b, i) => {
          const { text, to } = _b

          if (to) {
            const href = this.$router.resolve(to).href

            return {
              '@type': 'ListItem',
              position: i + 1,
              name: text,
              item: `${process.env.APP_URL}${href}`,
            }
          }

          return null
        })
        .filter((item) => item)

      return {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement,
        },
      }
    },
    getContinentTitle() {
      return this.$t(`${this.base}.title.continent`, {
        name: this.getItemDescription,
      })
    },
    getCountryTitle() {
      return this.$t(`${this.base}.title.country`, {
        name: this.getItemDescription,
      })
    },
    getItemDescription() {
      const {
        filters: { duration },
      } = this.searchForm

      let description = this.header.name

      if (
        duration.length === 2 &&
        duration.every((element) => element === duration[0])
      ) {
        const days = duration[0]
        const isWeek = days % 7 === 0

        description += ` ${this.$t(`${this.base}.title.rewriting.of`)} `

        if (isWeek) {
          description += this.$tc(
            `${this.base}.title.rewriting.weeks`,
            days / 7,
            {
              weeks: days / 7,
            }
          )
          description += ` (${this.$tc(
            `${this.base}.title.rewriting.days`,
            days,
            {
              days,
            }
          )})`
        } else {
          description += this.$tc(`${this.base}.title.rewriting.days`, days, {
            days,
          })
        }
      }

      return description
    },
  },
  watch: {
    $route: {
      handler(newValue, oldValue) {
        if (process.server) return

        if (
          typeof oldValue !== 'undefined' &&
          newValue.name === oldValue.name
        ) {
          this.getData()
        }
      },
    },
    loading: {
      immediate: true,
      handler(newValue, oldValue) {
        this.$nuxt.$emit('custom-loading', {
          enabled: newValue,
          source: newValue ? 'search' : null,
        })

        if (!newValue) {
          if (this.isOutletTest) {
            this.setOutletCover()
          } else if (this.isOutlet) {
            this.routeName = 'landing-permalink-test'
            this.setOutletCover()
          } else if (this.isMountainsInMexico) {
            this.setMountainsCover()
          } else if (this.isCategory) {
            this.setCategoryCover()
          }
        }
      },
    },
    /** https://michaelnthiessen.com/how-to-watch-nested-data-vue/ */
    // 'searchForm.filters': {
    //   // This will let Vue know to look inside the array
    //   deep: true,

    //   // We have to move our method to a handler field
    //   handler() {
    //     this.changeRoute(true)
    //   },
    // },
    /** https://michaelnthiessen.com/how-to-watch-nested-data-vue/ */
    // 'searchForm.sort': {
    //   // This will let Vue know to look inside the array
    //   deep: true,

    //   // We have to move our method to a handler field
    //   handler() {
    //     this.changeRoute()
    //   },
    // },
    // // 'searchForm.page'(pag) {
    //   if (pag !== 1) {
    //     this.changeRoute()
    //   }
    // },
    // 'searchForm.limit'() {
    //   this.changeRoute()
    // },
  },
  created() {
    this.$nuxt.$on('handle-filters', this.handleFilters)
  },
  mounted() {
    this.sendAnalytics()
  },
  async beforeDestroy() {
    this.$nuxt.$off('handle-filters')

    if (
      ![
        'outlet',
        'outlet-test',
        'buen-fin',
        'c-mountains-in-mexico',
        'search',
        'search-type',
      ].includes(this.$route.name)
    ) {
      await this.$store.dispatch('search/updatePaginate', {
        page: 1,
        limit: 30,
        total: 0,
      })
      /** seteando los valores por defecto */
      await this.$store.dispatch('search/setForm', {
        filters: {
          age: [],
          category: [],
          date: [],
          duration: [],
          month: [],
          city: [],
          language: [],
          price: { min: null, max: null, type: 'price' },
        },
      })
    }
  },
  methods: {
    async handleFilters() {
      await this.$nuxt.$emit('close-bottom-drawer')

      this.changeRoute(true)
    },
    openFilter() {
      this.$store.dispatch('navbar/toggle', {
        type: 'filter',
      })
    },
    changeRoute(hasFilter = false) {
      const filters = {}
      Object.keys(this.searchForm.filters)
        .filter((t) => {
          const filter = this.searchForm.filters[t]

          if (Array.isArray(filter)) {
            return filter.length > 0
          } else if (typeof filter === 'object' && filter !== null) {
            return filter.min > 0 || filter.max > 0
          }

          return false
        })
        .forEach((t) => {
          const filter = this.searchForm.filters[t]

          if (
            typeof filter === 'object' &&
            filter !== null &&
            !Array.isArray(filter)
          ) {
            Object.keys(filter).forEach((key) => {
              filters[`${t}.${key}`] = filter[key]
            })
          } else {
            filters[t] = filter
          }
        })

      filters.sort = `${this.searchForm.sort.term}__${this.searchForm.sort.direction}`
      filters.page = hasFilter ? 1 : this.searchForm.page
      filters.limit = this.searchForm.limit

      const newRoute = {
        name: this.$route.name,
        params: this.$route.params,
        query: Object.assign(
          {},
          this.$route.name === 'search'
            ? {
                tab: this.urlParams.tab,
                value: this.urlParams.value,
                type: this.urlParams.type,
              }
            : {},
          filters
        ),
      }

      this.$router.push(newRoute)
    },
    sendAnalytics() {
      /** @todo revisar si registra el evento en gtag.js */
      if (process.browser) {
        const qs = objet2qs(this.$route.query)

        if (typeof this.$gtag !== 'undefined') {
          this.$gtag('event', 'view_search_results', qs)

          const impressions = this.items.map((item, index) => {
            let price = item.total_amount

            if (item.promotions.length > 0) {
              const isCurrentPromotion =
                Date.parse(item.promotions[0].end) >= Date.now()

              if (item.promotions[0].rules.length > 0) {
                price = item.promotions[0].rules[0].condition.includes(
                  this.currency
                )
                  ? item.total_amount * (1 - item.promotions[0].value / 100)
                  : item.total_amount
              } else if (isCurrentPromotion) {
                price = item.total_amount * (1 - item.promotions[0].value / 100)
              }
            }

            return {
              name: item.title,
              id: item.uuid,
              price: price === 0 ? '' : price.toFixed(2).toString(),
              category: item.categories,
              list: this.$route.path,
              position:
                (this.paginator.page - 1) * this.paginator.limit + index + 1,
            }
          })

          this.$gtag('event', 'eec.impressionView', {
            ecommerce: {
              currencyCode: this.currency, // Currency (eg USD)
              impressions,
            },
            page_title: this.$metaInfo.title,
            page_location: window.location.href,
          })
        }

        if (typeof this.$fb !== 'undefined') {
          const params = {
            content_type: 'destination',
            content_ids: [] /** specific to the top 3-5 destinations listed in search results */,
            travel_start: null /** solo aplica cuando hay filtro de rango de fecha */,
            travel_end: null /** solo aplica cuando hay filtro de rango de fecha */,
            city: null /** solo aplica cuando se busca por ciudad */,
            region: null /** region or state => no aplica de momento */,
            country: null /** se obtiene del primer item */,
            num_adults: 1,
            num_children: null /** if applicable> */,
          }

          if (this.isOutlet || this.isBuenFin) {
            params.content_ids = this.items.map((item) => item.uuid)
            params.content_names = this.items.map((item) => item.title)
          } else {
            /** obtengo maximo 5 elementos listados */
            for (let i = 0; i < this.items.length && i < 5; i++) {
              params.content_ids.push(this.items[i].uuid)

              if (i === 0) {
                params.country = this.items[i].country.name
                params.city = this.items[i].starting_point
              }
            }
          }

          if (this.isCity && !params.city) {
            params.city = this.$route.params.value
          }

          if (this.searchForm.filters.date.length === 2) {
            params.travel_start = this.searchForm.filters.date[0]
            params.travel_end = this.searchForm.filters.date[1]
          }

          Object.keys(params)
            .filter(
              (t) => !['content_type', 'content_ids', 'num_adults'].includes(t)
            )
            .filter((t) => !params[t])
            .map((t) => delete params[t])

          if (this.isOutlet || this.isBuenFin) {
            this.$fb.track('ViewContent', params)
          } else {
            this.$fb.track('Search', params)
          }
        }

        if (typeof this.$tiktok !== 'undefined') {
          const contents = this.items.map((item) => ({
            content_id: item.uuid,
            content_type: item.type,
            content_name: item.title,
            content_country: item.country.name,
            content_city: item.starting_point,
            content_brand: item.is_lad ? 'localadventures' : 'third party',
          }))

          this.$tiktok.track('ViewSearchResults', {
            contents,
            query: qs,
          })
        }
      }
    },
    async getData() {
      /**
       * el metodo `getData` y `asyncData` tienen exactamente la misma logica
       * lo que uno es SSR y otro es para interactuar con los filtros
       */
      const hasQuery = !!this.$route.query
      const qParams = this.$route.params || {}
      const hasParams = !!qParams.type && !!qParams.value

      if (Object.entries(this.$route.query).length === 0) {
        await this.$store.dispatch('search/updatePaginate', {
          page: 1,
          limit: 30,
          total: 0,
        })
      }

      let params = { tab: undefined, type: undefined, value: undefined }
      let days = null

      switch (this.$route.name) {
        case 'outlet-test':
        case 'outlet':
        case 'buen-fin':
          params = { tab: 'experiences', type: 'outlet', value: 'experiences' }

          await this.$store.dispatch('search/setForm', {
            sort: 'more_popular__desc',
          })

          break
        case 'c-mountains-in-mexico':
          params = {
            tab: 'experiences',
            type: 'mountains-in-mexico',
            value: 'experiences',
          }
          break
        case 'search':
          // eslint-disable-next-line no-case-declarations
          const tab = hasQuery ? this.$route.query.tab : 'experiences'
          // eslint-disable-next-line no-case-declarations
          const type = hasQuery ? this.$route.query.type : 'experience'
          // eslint-disable-next-line no-case-declarations
          const value = hasQuery
            ? this.$route.query.q || this.$route.query.value
            : ''

          params = { tab, type, value }
          break
        case 'search-type':
        default:
          params = {
            tab: qParams.tab,
            type: qParams.type,
            value: qParams.value,
          }
          if (qParams.days !== undefined && typeof qParams.days === 'string') {
            const regex = new RegExp('^\\d?\\d')
            days = qParams.days.match(regex)
          }
          break
      }

      if (typeof SEARCH_TYPE_ROUTER.base[params.type] === 'string') {
        params.type = SEARCH_TYPE_ROUTER.base[params.type]
      }

      if (typeof SEARCH_TYPE_ROUTER.base[params.tab] === 'string') {
        params.tab = SEARCH_TYPE_ROUTER.base[params.tab]
      }

      /**
       * Transforms the given filters object into a new object with nested properties.
       *
       * @param {Object} filters - The filters object to be transformed.
       * @return {Object} The transformed filters object.
       */
      const transformFilters = (filters) => {
        const result = {}

        Object.keys(filters).forEach((key) => {
          if (key.includes('.')) {
            const [parent, child] = key.split('.')

            if (!result[parent]) {
              result[parent] = {}
            }
            result[parent][child] = filters[key]
          } else {
            result[key] = filters[key]
          }
        })

        return result
      }

      // eslint-disable-next-line no-case-declarations
      const filters = hasQuery
        ? transformFilters(Object.assign({}, this.$route.query))
        : {}
      const sort = this.$route.query.sort ? this.$route.query.sort : null
      if (days !== null) {
        filters.duration = [days[0], days[0]]
      }

      delete filters.tab
      delete filters.q
      delete filters.type
      // delete filters.page
      delete filters.limit
      delete filters.value

      if (hasQuery) {
        Object.keys(filters).forEach((k) => {
          if (['price'].includes(k)) {
            Object.keys(filters[k]).forEach((key) => {
              if (['min', 'max'].includes(key)) {
                filters[k][key] = parseInt(filters[k][key])
              }
            })
          } else if (!['value', 'q', 'sort', 'page', 'limit'].includes(k)) {
            /** transformando siempre a array los paramentros get */
            if (!Array.isArray(filters[k])) {
              filters[k] = ['category'].includes(k)
                ? [filters[k]]
                : [filters[k], filters[k]]
            }
            /** tranformando string a int */
            if (!['date', 'category', 'city', 'language'].includes(k)) {
              filters[k] = filters[k].map((item) => parseInt(item))
            }
          }
        })

        this.$store.dispatch('search/setForm', {
          search: this.$route.name === 'search' ? params.value : '',
          filters,
          sort,
        })

        if (
          this.$route.name === 'search' &&
          !['country'].includes(params.type)
        ) {
          this.$store.dispatch('search/setCover', {
            name: params.value,
          })
        }
      }

      if (
        hasParams ||
        (hasQuery && params.type !== undefined && params.value !== undefined)
      ) {
        this.loading = true

        const data = {
          tab: params.tab,
          type: params.type,
          value: params.value,
          form: this.$store.state.search.form,
        }

        await this.$store.dispatch('search/getExperiences', data)

        this.loading = false
        this.loaded = true
      }

      this.urlParams = params

      this.sendAnalytics()
    },
    setOutletCover() {
      const data = {
        name: this.$t(`route.outlet_index`),
        slug: 'outlet',
        description: this.$t(`${this.base}.meta.description`),
      }

      this.$store.dispatch('search/setCover', data)
    },
    setMountainsCover() {
      const data = {
        name: this.$t(`route.outlet_index`),
        slug: 'outlet',
        description: this.$t(`${this.base}.meta.description`),
      }

      this.$store.dispatch('search/setCover', data)
    },
    setCategoryCover() {
      const slug = this.$route.params.value.replace('-', '_')

      const data = {
        name: this.$t(`general.categories.${slug}`),
      }

      this.$store.dispatch('search/setCover', data)
    },
    async handleSizeChange(val) {
      await this.$store.dispatch('search/updatePaginate', {
        limit: val,
        page:
          this.searchForm.total / val < this.searchForm.page
            ? Math.trunc(this.searchForm.total / val)
            : this.searchForm.page,
      })
      this.changeRoute()
    },
    async handleCurrentChange(val) {
      await this.$store.dispatch('search/updatePaginate', {
        page: val,
      })
      this.changeRoute()
    },
    getDescriptionWithPage(description) {
      if (this.paginator.page > 1) {
        description = `${this.$t(`${this.base}.page`)} ${
          this.paginator.page
        }: ${description}`
      }

      return description
    },
  },
  head() {
    const staticMeta = StaticMetaMixin.head.call(this)
    let title = this.$t(`${this.base}.meta.title`)
    let description = this.$t(`${this.base}.meta.description`)

    if (this.isOutlet && this.paginator.page > 1) {
      description = this.getDescriptionOutlet
    } else if (this.isCountry && this.urlParams.tab === 'trips') {
      title = this.$t(`${this.base}.trips.title`, {
        name: this.header.name,
      })
      description = this.$t(`${this.base}.trips.description`, {
        country: this.header.name,
      })
    } else if (
      (this.isCountry || this.isContinent || this.isCity) &&
      this.urlParams.tab === 'flights'
    ) {
      title = this.$t(`${this.base}.flights.title`, {
        name: this.header.name,
      })
      description = this.$t(`${this.base}.flights.description`, {
        name: this.header.name,
      })
    } else if (this.isCity) {
      title = this.$t(`${this.base}.city.title`, {
        name: this.header.name,
      })
      description = this.getDescriptionWithPage(
        this.$t(`${this.base}.city.description`, {
          city: this.header.name,
        })
      )
    } else if (this.isCountry) {
      title = this.getCountryTitle
      description = this.$t(`${this.base}.title.rewriting.description`, {
        title: this.getItemDescription,
      })
    } else if (this.isContinent) {
      title = this.getContinentTitle
      description = this.$t(`${this.base}.title.rewriting.description`, {
        title: this.getItemDescription,
      })
    } else if (this.isBuenFin) {
      title = this.$t('buen_fin.meta.title')
      description = this.$t('buen_fin.meta.description')
    }

    title += ` | ${process.env.APP_NAME}`

    const meta = [
      {
        hid: 'og:title',
        property: 'og:title',
        content: title,
      },
      {
        hid: 'twitter:title',
        name: 'twitter:title',
        content: title,
      },
      {
        hid: 'og:description',
        property: 'og:description',
        content: description,
      },
      {
        hid: 'description',
        name: 'description',
        content: description,
      },
      {
        hid: 'twitter:description',
        property: 'twitter:description',
        content: description,
      },
    ]

    const others = staticMeta.meta.filter(
      (t) =>
        ![
          'og:title',
          'twitter:title',
          'og:description',
          'description',
          'twitter:description',
        ].includes(t.hid)
    )

    const language = process.env.APP_LOCALE.toLowerCase().split('_')[0]
    let baseType = null
    let baseTab = null
    for (const [key, value] of Object.entries(
      SEARCH_TYPE_ROUTER.alias[language]
    )) {
      if (value === this.$route.params.type) {
        baseType = key
      }
    }

    for (const [key, value] of Object.entries(
      SEARCH_TYPE_ROUTER.tabs[language]
    )) {
      if (value === this.$route.params.tab) {
        baseTab = key
      }
    }

    let link = []
    if (this.$route.name === 'search-type') {
      link = [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `${process.env.APP_URL}${this.$route.path}`,
        },
        {
          hid: 'alternate-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${process.env.APP_URL_EN_US}/${SEARCH_TYPE_ROUTER.alias.en[baseType]}/${this.header.permalink.en}/${SEARCH_TYPE_ROUTER.tabs.en[baseTab]}`,
        },
        {
          hid: 'alternate-en',
          rel: 'alternate',
          hreflang: 'en',
          href: `${process.env.APP_URL_EN_US}/${SEARCH_TYPE_ROUTER.alias.en[baseType]}/${this.header.permalink.en}/${SEARCH_TYPE_ROUTER.tabs.en[baseTab]}`,
        },
        {
          hid: 'alternate-es',
          rel: 'alternate',
          hreflang: 'es',
          href: `${process.env.APP_URL_ES_MX}/${SEARCH_TYPE_ROUTER.alias.es[baseType]}/${this.header.permalink.es}/${SEARCH_TYPE_ROUTER.tabs.es[baseTab]}`,
        },
        {
          hid: 'alternate-es-CO',
          rel: 'alternate',
          hreflang: 'es-CO',
          href: `${process.env.APP_URL_ES_CO}/${SEARCH_TYPE_ROUTER.alias.es[baseType]}/${this.header.permalink.es}/${SEARCH_TYPE_ROUTER.tabs.es[baseTab]}`,
        },
      ]
    } else {
      link = [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `${process.env.APP_URL}${this.$route.path}`,
        },
        {
          hid: 'alternate-default',
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${process.env.APP_URL_EN_US}${this.$route.path}`,
        },
        {
          hid: 'alternate-en',
          rel: 'alternate',
          hreflang: 'en',
          href: `${process.env.APP_URL_EN_US}${this.$route.path}`,
        },
        {
          hid: 'alternate-es',
          rel: 'alternate',
          hreflang: 'es',
          href: `${process.env.APP_URL_ES_MX}${this.$route.path}`,
        },
        {
          hid: 'alternate-es-CO',
          rel: 'alternate',
          hreflang: 'es-CO',
          href: `${process.env.APP_URL_ES_CO}${this.$route.path}`,
        },
      ]
    }

    const quotient = Math.floor(this.paginator.total / this.paginator.limit)
    const remainder = this.paginator.total % this.paginator.limit

    const totalPages = quotient + (remainder ? 1 : 0)

    if (totalPages > 1) {
      const hasQuery = !!this.$route.query

      const filters = hasQuery ? Object.assign({}, this.$route.query) : {}

      if (this.paginator.page > 1) {
        filters.page = this.paginator.page - 1

        const query = new URLSearchParams(filters).toString()

        link.push({
          rel: 'prev',
          href: `${process.env.APP_URL}${this.$route.path}?${query}`,
        })
      }

      if (this.paginator.page < totalPages) {
        filters.page = this.paginator.page + 1

        const query = new URLSearchParams(filters).toString()

        link.push({
          rel: 'next',
          href: `${process.env.APP_URL}${this.$route.path}?${query}`,
        })
      }
    }

    let script = []
    if (this.$route.name === 'search-type') {
      script = [this.getStructuredDataBreadcrumb]
    }

    if (this.items.length) {
      const itemListElement = this.items.map((item, index) => {
        return {
          '@context': 'http://schema.org/',
          '@type': 'Product',
          name: item.title,
          image: item.media.data.url,
          description: item.description,
          mpn: item.permalink,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: item.reviews.value || 3.6,
            reviewCount: item.reviews.count || 1,
          },
          availability: 'https://schema.org/InStock',
          priceCurrency: this.currency,
          price: item.total_amount.toFixed(2),
        }
      })

      script.push({
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement,
        },
      })
    }

    return {
      ...staticMeta,
      title,
      meta: [].concat(others, meta),
      link,
      script,
    }
  },
}
</script>

<style lang="scss">
.section--search {
  background-color: #f8f8f8;

  #container-section-search-navbar {
    .app--container-breadcrumb {
      padding-top: 0;
    }
  }

  .text-cover {
    height: 32vh;
    min-height: 300px;

    .el-container {
      position: relative;
      z-index: 2;
      color: white;
      height: 100%;

      .wrapper-header {
        position: absolute;
        bottom: 20px;
      }

      .hero-title {
        line-height: 1.2;
        text-shadow: 2px 2px 15px #464646;
        margin-bottom: 0.3em;
        font-size: 3em;
      }

      .hero-description {
        font-size: 1.5em;
        font-weight: 400;
        text-shadow: 2px 2px 15px #464646;
        font-style: italic;
        padding-right: 20px;
      }

      @media screen and (max-width: 700px) {
        .el-main {
          padding: 0 20px;
        }
      }

      @media screen and (max-width: 450px) {
        .hero-title {
          font-size: 2em;
        }
        .hero-description {
          font-size: 1.3em;
          margin-top: 0.3em;
          margin-bottom: 0;
          line-height: 1.68421053em;
        }
      }

      @media screen and (max-width: 320px) {
        .hero-description {
          display: none;
        }
      }
    }

    .image--cover img {
      position: absolute;
      z-index: 1;
      top: 0;
    }
  }

  .lad-container-media-text {
    &--left {
      order: 2;
    }

    &--right {
      order: 1;

      .svg-icon {
        font-size: 16em;
      }
    }
  }

  .affix {
    width: 100%;
    z-index: 15;

    .search-wrapper--navbar {
      border-bottom: 1px solid #ebeef5;
      border-top: 1px solid #ebeef5;
      position: absolute;
      width: 100%;
    }

    &.affix {
      .search-wrapper--navbar {
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }
    }
  }

  .experiences-section {
    background-color: #ebeef2;

    .search-wrapper--experiences {
      background-color: transparent;
      padding: 0;
      grid-gap: 20px;

      .wrapper--box-card-product {
        margin-top: 0;
      }
    }
    .section-buen-fin {
      margin-top: 20px;
      .buen-fin-description {
        text-align: justify;
      }
    }
  }

  .lad-experiences + .lad-experiences {
    margin-top: 20px;
  }

  .app--container-breadcrumb {
    padding: 60px 10px 0;

    @media (min-width: 992px) {
      padding-top: 0;
    }
  }

  .wrapper-paginate {
    margin-top: 30px;

    @media screen and (max-width: 700px) {
      text-align: right;

      .el-pager {
        .btn-quicknext,
        .number:not(.active) {
          display: none;
        }
      }

      .el-pagination__jump {
        display: none;
      }
    }

    .left-right {
      text-align: center;
      margin: 0 5px;
      background-color: #f4f4f5;
      color: #606266;
      min-width: 30px;
      border-radius: 2px;
    }
    .not-allowed {
      color: lightgrey;
      cursor: not-allowed;
    }
  }

  .lad-filter--date-range-content {
    .el-range-editor {
      visibility: hidden;
    }
  }

  .search-wrapper--filters {
    margin-bottom: 20px;
  }

  .hero-description-outlet {
    font-size: 18px;
    font-weight: unset;
  }

  @media screen and (max-width: 700px) {
    .country-header {
      .el-main {
        padding: 20px;
      }
    }
  }

  @media screen and (max-width: 450px) {
    .country-header {
      h1 {
        font-size: 1.6em;
      }
    }
  }

  &.is-outlet {
    background-color: #ebeef2;

    .country-header {
      h1 {
        font-size: 56px;
        font-weight: 700;
        line-height: 73.8px;
        color: #221f20;
        margin: 0;
      }

      h2 {
        font-size: 18px;
        font-weight: 500;
        line-height: 21.98px;
        color: #221f20;
        margin: 10px 0 0;
      }
    }
  }

  .wrapper-open-filter {
    padding: 20px 20px 0 20px;
    background-color: transparent;
  }

  .open-filter {
    margin: 20px 0 0;
    display: none;

    .el-button {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      background-color: transparent;
      color: #409eff;

      span {
        order: 1;
        flex-grow: 1;
        text-align: left;
      }

      i {
        order: 2;
        text-align: right;
      }
    }

    @media screen and (max-width: 1026px) {
      display: block;
    }
  }
}

.lad-country-information {
  & .el-main {
    padding-bottom: 0;
    overflow: unset;

    .link-more-information {
      margin-top: 20px;
      text-align: center;
    }

    .el-row {
      flex-wrap: wrap;
      // justify-content: center;

      & > .el-col {
        margin-bottom: 20px;
      }
    }
  }
}

.link-more-information {
  margin-top: 20px;
  text-align: center;
}

.el-aside.lad--custom__filters {
  width: 320px !important;
  box-sizing: border-box;
  padding: 20px 0 0;

  .search-container--filters {
    border-radius: 10px;
    width: 320px;
    box-sizing: border-box;
  }
}
</style>
